import { useEffect } from 'react'
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { LanguageProvider } from './hooks/useLanguage'
import { EventList } from './pages/EventList'
import { EventDetail } from './pages/EventDetail'
import { FollowSuggest } from './pages/FollowSuggest'
import { ExplainPage } from './pages/ExplainPage'
import { MyFollowsPage } from './pages/MyFollowsPage'
import { BalancePage } from './pages/BalancePage'
import { TimelineChatPage } from './pages/TimelineChatPage'
import { BackToTopButton } from './components/BackToTopButton'
import { SpendingPage } from './pages/SpendingPage'
import { MarketsPage } from './pages/MarketsPage'
import { MarketDetailPage } from './pages/MarketDetailPage'
import { PageAnalyticsTracker } from './components/PageAnalyticsTracker'
import { PolicyAnalysisPage } from './pages/PolicyAnalysisPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { CalendarPage } from './pages/CalendarPage'
import { MePage } from './pages/MePage'
import { SettingsPage } from './pages/SettingsPage'
import { startAnalyticsHeartbeat, trackAnalytics } from './utils/analytics'
import { TelegramLaunchRouter } from './components/TelegramLaunchRouter'
import { PrivacyConsentGate } from './components/PrivacyConsentGate'
import { PrivacyDataPage } from './pages/PrivacyDataPage'
import { WebAuthGate } from './components/WebAuthGate'

function AnalyticsLifecycle() {
  useEffect(() => {
    void trackAnalytics({
      event_type: 'session_start',
      path: window.location.pathname,
      data: {
        start_param: WebApp.initDataUnsafe?.start_param,
      },
    })

    const stopHeartbeat = startAnalyticsHeartbeat()

    let sessionEndSent = false

    const sendSessionEnd = (reason: string) => {
      if (sessionEndSent) return
      sessionEndSent = true
      void trackAnalytics({
        event_type: 'session_end',
        page: undefined,
        path: window.location.pathname,
        data: { reason },
      }, true)
    }

    const handleUnload = () => sendSessionEnd('beforeunload')
    const handlePageHide = () => sendSessionEnd('pagehide')
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionEnd('visibility_hidden')
        return
      }
      sessionEndSent = false
    }

    window.addEventListener('beforeunload', handleUnload)
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      stopHeartbeat()
      window.removeEventListener('beforeunload', handleUnload)
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return null
}

function App() {
  useEffect(() => {
    WebApp.ready()
    WebApp.expand()

    const theme = WebApp.themeParams
    if (theme.bg_color) document.documentElement.style.setProperty('--tg-bg-color', theme.bg_color)
    if (theme.text_color) document.documentElement.style.setProperty('--tg-text-color', theme.text_color)
    WebApp.HapticFeedback.impactOccurred('light')
  }, [])

  return (
    <LanguageProvider>
      <WebAuthGate>
      <PrivacyConsentGate>
        <BrowserRouter>
          <AnalyticsLifecycle />
          <TelegramLaunchRouter />
          <PageAnalyticsTracker />
          <Routes>
            <Route path="/" element={<EventList />} />
            <Route path="/event/:id" element={<EventDetail />} />
            <Route path="/event/:id/explain" element={<ExplainPage />} />
            <Route path="/event/:id/policy-analysis" element={<PolicyAnalysisPage />} />
            <Route path="/event/:id/timeline-chat" element={<TimelineChatPage />} />
            <Route path="/event/:id/follow-suggest" element={<FollowSuggest />} />
            <Route path="/follows" element={<MyFollowsPage />} />
            <Route path="/balance" element={<BalancePage />} />
            <Route path="/spending" element={<SpendingPage />} />
            <Route path="/markets" element={<MarketsPage />} />
            <Route path="/markets/:eventId" element={<MarketDetailPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/event-plan" element={<Navigate to="/calendar?planner=1" replace />} />
            <Route path="/me" element={<MePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/privacy" element={<PrivacyDataPage />} />
          </Routes>
          <BackToTopButton />
        </BrowserRouter>
      </PrivacyConsentGate>
      </WebAuthGate>
    </LanguageProvider>
  )
}

export default App
